import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../lib/api-client';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { CredentialDialog, type CredentialInfo } from '../../components/CredentialDialog';

interface SubMdrItem {
  id: string;
  username: string;
  displayName: string;
  status: 'active' | 'inactive';
  mustChangePassword: boolean;
  createdAt: string;
}

function sanitizeForUsername(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function SubMdrDialog({
  onClose,
  onCredential,
}: {
  onClose: () => void;
  onCredential: (c: CredentialInfo) => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ username: '', displayName: '', password: '' });
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [usernameEditable, setUsernameEditable] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Prefix the username with the master MDR's own username (e.g. "masterroxy-")
  // to avoid collisions across masters — chains the display name like the
  // sub-seller create flow. The master can still override it.
  const masterPrefix = user?.username ? sanitizeForUsername(user.username) : '';
  useEffect(() => {
    if (usernameTouched) return;
    const parts = [masterPrefix, sanitizeForUsername(form.displayName)].filter(Boolean);
    setForm((p) => ({ ...p, username: parts.join('-') }));
  }, [form.displayName, masterPrefix, usernameTouched]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiPost<SubMdrItem & { tempPassword?: string }>('/mdr/sub-mdrs', {
        username: form.username,
        displayName: form.displayName,
        password: form.password || undefined,
      }),
    onSuccess: (res) => {
      if (!res.ok) {
        setServerError(res.error.message);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['mdr', 'sub-mdrs'] });
      if (res.data.tempPassword) {
        onCredential({
          name: res.data.displayName,
          username: res.data.username,
          tempPassword: res.data.tempPassword,
        });
      }
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('mdr.addSubMdr')}
        </h3>
        {serverError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
            {serverError}
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setServerError(null);
            saveMutation.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('auth.username')}
              </label>
              <label className="flex items-center gap-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={usernameEditable}
                  onChange={(e) => {
                    const editable = e.target.checked;
                    setUsernameEditable(editable);
                    // Editing manually stops auto-prefill; unchecking resumes it.
                    setUsernameTouched(editable);
                  }}
                  className="rounded border-gray-300"
                />
                {t('mdr.editUsername')}
              </label>
            </div>
            <Input
              value={form.username}
              disabled={!usernameEditable}
              onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('mdr.displayName')}
            </label>
            <Input
              value={form.displayName}
              onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('users.passwordOptional')}
            </label>
            <Input
              type="text"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              placeholder={t('users.passwordPlaceholder')}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saveMutation.isPending} className="flex-1">
              {saveMutation.isPending ? t('common.loading') : t('common.save')}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function MdrSubMdrs() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [credential, setCredential] = useState<CredentialInfo | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['mdr', 'sub-mdrs'],
    queryFn: () => apiGet<{ items: SubMdrItem[]; total: number }>('/mdr/sub-mdrs'),
  });

  const toggleStatus = useMutation({
    mutationFn: (s: SubMdrItem) =>
      apiPatch(`/mdr/sub-mdrs/${s.id}`, {
        status: s.status === 'active' ? 'inactive' : 'active',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mdr', 'sub-mdrs'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/mdr/sub-mdrs/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mdr', 'sub-mdrs'] }),
  });

  const resetMutation = useMutation({
    mutationFn: (s: SubMdrItem) =>
      apiPost<{ username: string; tempPassword: string }>(
        `/mdr/sub-mdrs/${s.id}/reset-password`,
      ),
    onSuccess: (res, s) => {
      if (res.ok) {
        setCredential({
          name: s.displayName,
          username: res.data.username,
          tempPassword: res.data.tempPassword,
        });
      }
    },
  });

  const items = data?.ok ? data.data.items : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {t('mdr.subMdrsTitle')}
        </h1>
        <Button size="sm" onClick={() => setShowForm(true)}>
          {t('mdr.addSubMdr')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {t('mdr.subMdrsTitle')} ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <div className="p-4 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && items.length === 0 && (
            <div className="p-4 text-gray-400">{t('common.noData')}</div>
          )}
          {items.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('mdr.displayName')}</TableHead>
                    <TableHead>{t('auth.username')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.displayName}</TableCell>
                      <TableCell className="font-mono text-xs">{s.username}</TableCell>
                      <TableCell>
                        <Badge variant={s.status === 'active' ? 'success' : 'danger'}>
                          {t(`common.${s.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="space-x-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => toggleStatus.mutate(s)}>
                          {s.status === 'active' ? t('common.deactivate') : t('common.activate')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={resetMutation.isPending}
                          onClick={() => {
                            if (confirm(t('sellers.confirmReset', { name: s.displayName })))
                              resetMutation.mutate(s);
                          }}
                        >
                          {t('sellers.resetPassword')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm(t('mdr.deleteSubConfirm', { name: s.displayName })))
                              deleteMutation.mutate(s.id);
                          }}
                        >
                          {t('common.delete')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {showForm && (
        <SubMdrDialog onClose={() => setShowForm(false)} onCredential={setCredential} />
      )}

      {credential && (
        <CredentialDialog credential={credential} onClose={() => setCredential(null)} />
      )}
    </div>
  );
}
